import apiClient from './client';
import type { SessionResponse, SubmitAnswerResponse } from '../types/problems';
import type {
  ThirdLevelBidSessionResponse,
  ThirdLevelSubMode,
  ThirdLevelEditorCategory,
  ThirdLevelEditorAssignDifficultyResponse,
  ThirdLevelEditorAnswerResponse,
} from '../types/thirdLevelBid';

/**
 * Isolated data calls for "Decision to make a 3rd level bid" (feature 013). Hits the feature's
 * own endpoints (NOT the Overcalling/Problems endpoints) and adapts the `{ items }` wire shape
 * to the reused session hook's `{ problems }` SessionResponse so the shared UI is unchanged.
 */
export const thirdLevelBidApi = {
  /**
   * The eligible pool for one opener-based sub-mode, ordered by role server-side (FR-010).
   * Omitting `subMode` returns the whole pool (legacy). For `precision`, `openingBid` is one
   * of `1C`, `1D`, `1H,1S`, `1NT`, `2C`, `2D` (FR-022/FR-024/FR-025, Session 2026-06-08).
   */
  getCandidates: async (
    subMode?: ThirdLevelSubMode,
    openingBid?: string,
    category?: ThirdLevelEditorCategory,
  ): Promise<SessionResponse> => {
    const params: Record<string, string> = {};
    if (subMode) params.subMode = subMode;
    if (openingBid) params.openingBid = openingBid;
    // Editor mode only (FR-028): the server ignores `category` for public callers.
    if (category) params.category = category;

    const response = await apiClient.get<ThirdLevelBidSessionResponse>(
      '/third-level-bid-decision/candidates',
      { params },
    );
    return { problems: response.data.items, totalAvailable: response.data.totalAvailable };
  },

  /**
   * Dev-only `?id=` locator (FR-018): a single masked item by tableresult id. Server gates on
   * the EnableSerialSearch flag (403 when off) and 404s an ineligible id.
   */
  getById: async (tableResultId: number): Promise<SessionResponse> => {
    const response = await apiClient.get<ThirdLevelBidSessionResponse>(
      `/third-level-bid-decision/by-id/${tableResultId}`,
    );
    return { problems: response.data.items, totalAvailable: response.data.totalAvailable };
  },

  /** Scores the user's call against Jeff's actual decision (FR-014). Public callers are recorded. */
  submitAnswer: async (tableResultId: number, bid: string): Promise<SubmitAnswerResponse> => {
    const response = await apiClient.post<SubmitAnswerResponse>(
      `/third-level-bid-decision/${tableResultId}/answer`,
      { bid },
    );
    return response.data;
  },

  /**
   * Feature 019 edition (2026-07-08): record a public user's SKIP so the hand is suppressed from
   * future sessions, exactly like an answer. Editing-granted callers are a no-op server-side.
   */
  skip: async (tableResultId: number): Promise<void> => {
    await apiClient.post(`/third-level-bid-decision/${tableResultId}/skip`);
  },

  /**
   * Editor mode (FR-029): assign the GLOBAL level of difficulty (1-10) to a hand. Admin/Editor
   * only (403 otherwise); writes to hawkes_bridge, last-write-wins.
   */
  assignDifficulty: async (
    tableResultId: number,
    level: number,
  ): Promise<ThirdLevelEditorAssignDifficultyResponse> => {
    const response = await apiClient.post<ThirdLevelEditorAssignDifficultyResponse>(
      `/third-level-bid-decision/${tableResultId}/difficulty`,
      { level },
    );
    return response.data;
  },

  /**
   * Editor mode (FR-028): record the calling editor's own answer for a hand (per-editor) so it
   * flips to "Answered" for that editor. Admin/Editor only. Records no learner history (FR-013).
   */
  submitEditorAnswer: async (
    tableResultId: number,
    bid: string,
  ): Promise<ThirdLevelEditorAnswerResponse> => {
    const response = await apiClient.post<ThirdLevelEditorAnswerResponse>(
      `/third-level-bid-decision/${tableResultId}/editor-answer`,
      { bid },
    );
    return response.data;
  },
};
